import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #24292f;
`;

const Content = styled.div`
  padding: 20px;
`;

const ExportOption = styled.button`
  width: 100%;
  padding: 16px;
  background: white;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    border-color: #0969da;
    background: #f6f8fa;
  }
`;

const Icon = styled.div`
  font-size: 24px;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
  margin-bottom: 4px;
`;

const OptionDesc = styled.div`
  font-size: 12px;
  color: #586069;
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid #d0d7de;
  background: white;
  color: #24292f;
  
  &:hover {
    background: #f6f8fa;
  }
`;

function ExportModal({ spec, onClose }) {
  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onClose();
  };

  const exportYAML = () => {
    downloadFile(spec, 'openapi-spec.yaml', 'application/x-yaml');
  };

  const exportJSON = () => {
    try {
      const parsed = yaml.load(spec);
      const json = JSON.stringify(parsed, null, 2);
      downloadFile(json, 'openapi-spec.json', 'application/json');
    } catch (e) {
      alert('Invalid spec format');
    }
  };

  const exportPostman = () => {
    try {
      const parsed = yaml.load(spec);
      const collection = {
        info: {
          name: parsed.info?.title || 'API Collection',
          description: parsed.info?.description || '',
          schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json'
        },
        item: []
      };

      for (const path in parsed.paths || {}) {
        for (const method in parsed.paths[path]) {
          if (['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
            const op = parsed.paths[path][method];
            collection.item.push({
              name: op.summary || `${method.toUpperCase()} ${path}`,
              request: {
                method: method.toUpperCase(),
                header: [],
                url: {
                  raw: `{{baseUrl}}${path}`,
                  host: ['{{baseUrl}}'],
                  path: path.split('/').filter(p => p)
                },
                description: op.description || ''
              }
            });
          }
        }
      }

      downloadFile(JSON.stringify(collection, null, 2), 'postman-collection.json', 'application/json');
    } catch (e) {
      alert('Failed to generate Postman collection');
    }
  };

  const exportHTML = () => {
    try {
      const parsed = yaml.load(spec);
      const html = `<!DOCTYPE html>
<html>
<head>
  <title>${parsed.info?.title || 'API Documentation'}</title>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 1200px; margin: 0 auto; padding: 20px; }
    h1 { color: #24292f; }
    h2 { color: #0969da; margin-top: 32px; }
    .endpoint { background: #f6f8fa; padding: 16px; border-radius: 6px; margin: 12px 0; }
    .method { display: inline-block; padding: 4px 8px; border-radius: 4px; font-weight: 600; color: white; }
    .get { background: #28a745; }
    .post { background: #0969da; }
    .put { background: #fb8500; }
    .delete { background: #cf222e; }
  </style>
</head>
<body>
  <h1>${parsed.info?.title || 'API Documentation'}</h1>
  <p>${parsed.info?.description || ''}</p>
  <p><strong>Version:</strong> ${parsed.info?.version || '1.0.0'}</p>
  ${Object.entries(parsed.paths || {}).map(([path, methods]) => 
    Object.entries(methods).filter(([m]) => ['get','post','put','delete','patch'].includes(m))
      .map(([method, op]) => `
        <div class="endpoint">
          <span class="method ${method}">${method.toUpperCase()}</span>
          <strong>${path}</strong>
          <p>${op.summary || ''}</p>
          ${op.description ? `<p>${op.description}</p>` : ''}
        </div>
      `).join('')
  ).join('')}
</body>
</html>`;
      downloadFile(html, 'api-documentation.html', 'text/html');
    } catch (e) {
      alert('Failed to generate HTML documentation');
    }
  };

  const exportCurl = () => {
    try {
      const parsed = yaml.load(spec);
      const baseUrl = parsed.servers?.[0]?.url || 'https://api.example.com';
      let commands = `# API cURL Commands\n# Generated from ${parsed.info?.title || 'OpenAPI Spec'}\n\n`;

      for (const path in parsed.paths || {}) {
        for (const method in parsed.paths[path]) {
          if (['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
            const op = parsed.paths[path][method];
            commands += `# ${op.summary || method.toUpperCase() + ' ' + path}\n`;
            commands += `curl -X ${method.toUpperCase()} "${baseUrl}${path}"`;
            if (['post', 'put', 'patch'].includes(method)) {
              commands += ` \\\n  -H "Content-Type: application/json" \\\n  -d '{}'`;
            }
            commands += '\n\n';
          }
        }
      }

      downloadFile(commands, 'api-commands.sh', 'text/plain');
    } catch (e) {
      alert('Failed to generate cURL commands');
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>📤 Export Options</Title>
        </Header>
        <Content>
          <ExportOption onClick={exportYAML}>
            <Icon>📄</Icon>
            <OptionContent>
              <OptionTitle>YAML File</OptionTitle>
              <OptionDesc>Download as OpenAPI YAML specification</OptionDesc>
            </OptionContent>
          </ExportOption>

          <ExportOption onClick={exportJSON}>
            <Icon>📋</Icon>
            <OptionContent>
              <OptionTitle>JSON File</OptionTitle>
              <OptionDesc>Download as OpenAPI JSON specification</OptionDesc>
            </OptionContent>
          </ExportOption>

          <ExportOption onClick={exportPostman}>
            <Icon>🚀</Icon>
            <OptionContent>
              <OptionTitle>Postman Collection</OptionTitle>
              <OptionDesc>Import into Postman for API testing</OptionDesc>
            </OptionContent>
          </ExportOption>

          <ExportOption onClick={exportHTML}>
            <Icon>🌐</Icon>
            <OptionContent>
              <OptionTitle>HTML Documentation</OptionTitle>
              <OptionDesc>Static HTML page with API docs</OptionDesc>
            </OptionContent>
          </ExportOption>

          <ExportOption onClick={exportCurl}>
            <Icon>💻</Icon>
            <OptionContent>
              <OptionTitle>cURL Commands</OptionTitle>
              <OptionDesc>Shell script with all API endpoints</OptionDesc>
            </OptionContent>
          </ExportOption>
        </Content>
        <Footer>
          <Button onClick={onClose}>Close</Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default ExportModal;
